import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface AssignmentDetail {
  readonly assignmentId: string;
  readonly jobRequestId: string;
  readonly jobCode: string;
  readonly status: string;
  readonly priority: string;
  readonly territoryId: string;
  readonly acceptanceDueAt: string;
  readonly expectedStartDate: string;
  readonly expectedCompletionDate: string;
  readonly areaApproved: number;
  readonly siteAddress: string;
}

export function AssignmentAcceptPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [a, setA] = useState<AssignmentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiRequest<AssignmentDetail>(`/v1/vendor/assignments/${id}`, { token });
      setA(r);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, token]);

  async function accept() {
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/vendor/assignments/${id}/accept`, { method: 'POST', token, body: { remarks } });
      setInfo('Accepted');
      navigate('/vendor/portal/painters');
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function reject() {
    if (!reason) { setError('Reason required'); return; }
    if (!remarks) { setError('Remarks required'); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/vendor/assignments/${id}/reject`, { method: 'POST', token, body: { reasonCode: reason, remarks } });
      setInfo('Rejected');
      navigate('/vendor/portal/inbox');
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  async function clarify() {
    if (!subject) { setError('Subject required'); return; }
    if (!question) { setError('Question required'); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/vendor/assignments/${id}/clarify`, { method: 'POST', token, body: { subject, questionText: question } });
      setInfo('Clarification raised');
      setSubject(''); setQuestion('');
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  if (error && !a) return <main aria-label="assignment-accept"><p role="alert">{error}</p></main>;
  if (!a) return <main aria-label="assignment-accept"><p>Loading…</p></main>;

  return (
    <main aria-label="assignment-accept">
      <h1>{a.jobCode}</h1>
      <p aria-label="assign-status">Status: {a.status}</p>
      <p aria-label="assign-priority">Priority: {a.priority}</p>
      <p aria-label="assign-territory">Territory: {a.territoryId}</p>
      <p aria-label="assign-area">Area approved: {a.areaApproved} m²</p>
      <p aria-label="assign-dates">Start: {a.expectedStartDate} · Complete: {a.expectedCompletionDate}</p>
      <p aria-label="assign-due">Acceptance due: {a.acceptanceDueAt}</p>
      <p aria-label="assign-address">Site: {a.siteAddress}</p>

      {error ? <p role="alert">{error}</p> : null}
      {info ? <p role="status">{info}</p> : null}

      <section aria-label="decision-section">
        <label htmlFor="remarks">Remarks</label>
        <textarea id="remarks" aria-label="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        <label htmlFor="reason">Reject reason code</label>
        <input id="reason" aria-label="reason-code" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button onClick={accept} disabled={busy} aria-label="accept-btn">{busy ? '…' : 'Accept'}</button>
        <button onClick={reject} disabled={busy} aria-label="reject-btn">{busy ? '…' : 'Reject'}</button>
      </section>

      <section aria-label="clarify-section">
        <h2>Raise clarification</h2>
        <label htmlFor="cl-subj">Subject</label>
        <input id="cl-subj" aria-label="clarify-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label htmlFor="cl-q">Question</label>
        <textarea id="cl-q" aria-label="clarify-question" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button onClick={clarify} disabled={busy} aria-label="clarify-btn">Send</button>
      </section>
    </main>
  );
}
